import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faPencilAlt,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM } from '@/shared/constants';
import { TopMenu } from '@/shared/layout/TopMenu';
import { Table, type TableColumn } from '@/shared/ui/Table';
import { snackbar } from '@/shared/ui/snackbar';
import plusIcon from '@/assets/images/plus.svg';
import { searchAttributesQueryOptions, type SearchAttribute } from './api';
import { ConfirmDeleteSearchAttribute } from './ConfirmDeleteSearchAttribute';
import { SearchAttributeDialog } from './SearchAttributeDialog/SearchAttributeDialog';
import { searchAttributeTranslationUrl } from './translation-key';

export function SearchAttributesPage() {
  const searchAttributes = useQuery(searchAttributesQueryOptions());
  useEffect(() => {
    if (searchAttributes.isError)
      snackbar('Failed to load filters, please try again later');
  }, [searchAttributes.isError]);

  const [editDialogItem, setEditDialogItem] = useState<
    SearchAttribute | null | undefined
  >(undefined);
  const [removingDialogItem, setRemovingDialogItem] =
    useState<SearchAttribute | null>(null);

  const columns: readonly TableColumn<SearchAttribute>[] = [
    {
      id: 'name',
      header: 'Name',
      width: '30%',
      render: (searchAttribute) => (
        <span title={searchAttribute.name}>{searchAttribute.name}</span>
      ),
    },
    {
      id: 'product-lines',
      header: 'Product lines',
      width: '25%',
      render: (searchAttribute) => {
        const productLines = searchAttribute.productLines.join(', ');
        return <span title={productLines}>{productLines}</span>;
      },
    },
    {
      id: 'allowed-values',
      header: 'Allowed filter values',
      width: '30%',
      render: (searchAttribute) => {
        const allowedValues = searchAttribute.allowedValues
          .map(({ value }) => value)
          .join(', ');
        return <span title={allowedValues}>{allowedValues}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      width: '15%',
      render: (searchAttribute) => (
        <div className="edit-action">
          <a
            href={searchAttributeTranslationUrl(searchAttribute.translationKey)}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open translations for ${searchAttribute.name}`}
            className="action-icon mr-2"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
          <button
            type="button"
            className="action-icon"
            aria-label={`Edit ${searchAttribute.name}`}
            onClick={() => setEditDialogItem(searchAttribute)}
          >
            <FontAwesomeIcon icon={faPencilAlt} />
          </button>
          <button
            type="button"
            className="action-icon ml-2"
            aria-label={`Delete ${searchAttribute.name}`}
            onClick={() => setRemovingDialogItem(searchAttribute)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <div className="summary-table my-12">
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          onClick={() => setEditDialogItem(null)}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a filter</span>
        </button>
        <Table
          rows={searchAttributes.data ?? []}
          columns={columns}
          rowKey={(searchAttribute) => searchAttribute.id}
          ariaLabel="Search attributes"
          caption="Search attributes"
        />
      </div>
      {editDialogItem !== undefined && (
        <SearchAttributeDialog
          item={editDialogItem}
          onClose={() => setEditDialogItem(undefined)}
        />
      )}
      {removingDialogItem && (
        <ConfirmDeleteSearchAttribute
          item={removingDialogItem}
          onClose={() => setRemovingDialogItem(null)}
        />
      )}
    </div>
  );
}
